import React, { useState, useEffect, useCallback, useRef } from 'react';

import { 
  Character,
  CharacterFeature,
  CharacterItem,
  CharacterSheetInterface,
  CharacterSpecialFeature,
  CharacterSpell,
  PseudoItem,
  SpecialFeature,
  SummonOption2,
} from "@dndModels";
import { ModelBase } from "@models";

import DandDAPI from "@utilities/smart_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DataUtilities from '@dndUtilities/data_utilities';

import LoadingBox from "@components/input/LoadingBox";
import CharacterListOptionsInput from "@dndComponents/character/misc/CharacterListOptionsInput";

interface Props {
  type: string;
  char: CharacterSheetInterface;
  obj: CharacterFeature;
  onChange: () => void;
  options?: string[];
  source_id: string;
}

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterSpecialFeatureBox({
  type,
  char,
  obj,
  onChange,
  options = [],
  source_id,
}: Props) {
  const fos = obj.feature_options as CharacterSpecialFeature[];
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [special_features, setSpecialFeatures] = useState<SpecialFeature[]>([]);
  const [changing, setChanging] = useState(false);
  const [selected, setSelected] = useState(fos.filter(o => o.source_id !== "").map(o => o.source_id));

  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = useCallback(() => {
    setReloading(true);
  }, [setReloading]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const load = useCallback(() => {
    let new_sfs = [...data_util.special_features];
    if (options.length > 0) {
      new_sfs = new_sfs.filter(o => options.includes(o._id));
    } else if (type !== "") {
      new_sfs = new_sfs.filter(o => o.special_feature_type === type);
    }
    new_sfs = new_sfs.filter(o => 
      char_util.fitLimits(char, o.limitations, "special_feature", o._id) === "true");
    setSpecialFeatures(new_sfs);
    setFetched(true);
  }, [char, options, type]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
      char.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    }
  }, [char, fetched, setFetched, mounted, recalculated, load]);


  const change = useCallback(async () => {
    const new_obj = obj;
    const new_fos = new_obj.feature_options as CharacterSpecialFeature[];
    const removed = new_fos.filter(o => !selected.includes(o.source_id));
    const added = selected.filter(o => new_fos.filter(o2 => o2.source_id === o).length === 0);
    if (removed.length > 0) {
      for (const cfs of removed) {
        if (cfs.source) {
          // console.log(char_subrace);
          if (char instanceof Character) {
            for (const char_item of char.inventory.items) {
              // console.log(char_item);
              char_item.char_feature_bases = char_item.char_feature_bases.filter(o => 
                o.ancestries.filter(o2 => o2.true_id === cfs.true_id).length === 0);
              for (const scfs of char_item.sub_char_feature_sources) {
                scfs.char_feature_bases = scfs.char_feature_bases.filter(o => 
                  o.ancestries.filter(o2 => o2.true_id === cfs.true_id).length === 0);
              }
            }
          }
          char_util.removeCharFeatureBasesFor(char, cfs);
          cfs.char_feature_bases = [];
          cfs.source_id = "";
          cfs.source_name = "";
          cfs.source_type = "";
          cfs.source = null;
        }
      }
      onChange();
    }
    if (added.length > 0) {
      for (const id of added) {
        // Get an empty char_subrace and set the selected_subrace to it
        const cfs = new_fos.find(o => o.source_id === "");
        const selected_sf = data_util.special_features.find(o => o._id === id);
        if (cfs && selected_sf) {
          await api.loadObject(selected_sf);
          await cfs.copy_source({ char, feature_source: selected_sf, parent_cf: obj });
        }
      }
      onChange();
    }
    char.changed();
  }, [char, obj, onChange, selected]);

  useEffect(() => {
    if (changing) {
      setChanging(false);
      // debugger;
      change();
    }
  }, [changing, setChanging, change]);

  if (!fetched) {
    return (<LoadingBox />);
  } else {
    if (obj.feature) {
      const choice_count = obj.feature_options.length;
      // console.log(special_features);
      // console.log(options);
      // const sf_options = special_features.filter(o => 
      //   options.includes(o._id) &&
      //   char_util.fitLimits(character, o.limitations, "special_feature") === "true");
      return (
        <>
          <CharacterListOptionsInput
            data_type="special_feature"
            char={char}
            choose_max={choice_count} 
            use_objects={special_features}
            selected={selected} 
            onChange={(changed: string[]) => {
              setSelected(changed);
              setChanging(true);
            }} 
            getDisabledReasons={(obj) => {
              if (obj instanceof SpecialFeature) {
                // if (obj.name.includes("Ascendant Step")) {
                //   debugger;
                // }
                const reasons = char_util.fitLimits(char, obj.limitations, "special_feature", obj._id);
                return reasons === "true" ? "" : reasons;
              }
              return "";
            }}
          />
        </>
      );
    }
    return <></>;
  }
}
